import { useEffect } from "react";

/**
 * Custom hook to sync state with localStorage
 * @param {string} key - localStorage key
 * @param {*} value - current state value
 * @param {function} setter - state setter function
 * @param {*} fallbackValue - fallback value if localStorage is empty
 * @param {boolean} isJson - whether to JSON parse/stringify the value
 */
export const useLocalStorageSync = (
  key,
  value,
  setter,
  fallbackValue,
  isJson = false
) => {
  // Load from localStorage on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(key);
      if (!savedData) return;

      if (isJson) {
        const parsed = JSON.parse(savedData);
        if (parsed && typeof parsed === "object") {
          // Skip empty objects for selectedChoices
          if (
            key === "introQuestions_selectedChoices" &&
            !Array.isArray(parsed) &&
            Object.keys(parsed).length === 0
          ) {
            return;
          }
          setter(parsed);
        }
      } else {
        setter(savedData);
      }
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      try {
        localStorage.removeItem(key);
      } catch (removeError) {
        console.error(`Failed to remove corrupted ${key}:`, removeError);
      }
      setter(fallbackValue);
    }
  }, []); // Only run on mount

  // Save to localStorage when value changes
  useEffect(() => {
    try {
      if (isJson) {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        if (value) {
          localStorage.setItem(key, value);
        }
      }
    } catch (error) {
      console.error(`Failed to save ${key} to localStorage:`, error);
    }
  }, [key, value, isJson]);
};

/**
 * Custom hook to cleanup old KYC data from localStorage
 * This removes data in the old format (underscore-based slugs)
 */
export const useCleanupOldKycData = () => {
  useEffect(() => {
    const keysToCheck = [
      "introQuestions_employmentStatus",
      "introQuestions_annualIncome",
      "introQuestions_fundingSource",
      "introQuestions_liquidNetWorth",
    ];

    keysToCheck.forEach((key) => {
      try {
        const value = localStorage.getItem(key);
        // If value contains underscore format (old slug format), clear it
        if (value && value.includes("_") && !value.includes("$")) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.error(`Error checking ${key}:`, error);
      }
    });
  }, []);
};
